import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import GHLNotConnected from "@/components/GHLNotConnected";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Clock, User, Loader2 } from "lucide-react";

export default function Appointments() {
  const now = Date.now();
  const start = now - 7 * 24 * 3600 * 1000;
  const end = now + 30 * 24 * 3600 * 1000;

  const { data, isLoading, error } = useQuery({
    queryKey: ["ghl-appointments"],
    queryFn: async () => (await api.get(`/ghl/appointments?start_ms=${start}&end_ms=${end}`)).data,
    retry: false,
  });

  const notConnected = error?.response?.status === 400;
  const events = data?.events || data?.appointments || (Array.isArray(data) ? data : []);

  return (
    <div data-testid="appointments-page">
      <PageHeader title="Appointments" subtitle="Counselling sessions synced live from your GoHighLevel calendar." />

      {isLoading ? (
        <div className="flex items-center gap-3 text-navy-500"><Loader2 className="h-5 w-5 animate-spin text-gold-500" /> Loading from GoHighLevel…</div>
      ) : notConnected ? (
        <GHLNotConnected icon={CalendarDays} title="Connect your counselling calendar" description="Link your GoHighLevel sub-account to see upcoming counselling appointments here — pulled securely from your GHL calendar." />
      ) : error ? (
        <Card className="rounded-2xl border-navy-900/5 bg-white p-6 text-navy-600" data-testid="ghl-error">
          Couldn't load appointments from GoHighLevel. {error.response?.data?.detail || "Please check your connection in Settings."}
        </Card>
      ) : events.length === 0 ? (
        <Card className="rounded-2xl border-navy-900/5 bg-white p-10 text-center text-navy-500">No appointments in the selected window.</Card>
      ) : (
        <div className="space-y-3">
          {events.map((e, i) => (
            <Card key={e.id || i} data-testid={`appointment-${i}`} className="flex items-center gap-4 rounded-2xl border-navy-900/5 bg-white p-5 shadow-ambient">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gold-50"><CalendarDays className="h-5 w-5 text-gold-600" /></div>
              <div className="flex-1">
                <div className="font-semibold text-navy-900">{e.title || e.appointmentStatus || "Counselling Session"}</div>
                <div className="mt-1 flex flex-wrap gap-4 text-sm text-navy-500">
                  {(e.startTime || e.dateAdded) && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {new Date(e.startTime || e.dateAdded).toLocaleString()}</span>}
                  {(e.contactName || e.contact?.name) && <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> {e.contactName || e.contact?.name}</span>}
                </div>
              </div>
              {e.appointmentStatus && <Badge className="border-0 bg-navy-50 text-navy-700">{e.appointmentStatus}</Badge>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
